// pkg100 — re-extract is now a gated background job (D-100e/g): a zero-spend quote prices the run (1 or 2 credits by document size, free-action-aware) inside the existing confirm; on OK the job starts and the page returns to the module list — the module keeps serving its current version until the fresh extraction lands and returns it to draft — delivered 2026-07-18
// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg59 — flow consolidation package 1 (D-58a–d): Signatures panel (client-side detection lens; rows write the same run.sig / remap-with-src the chips write), five settings strips consolidated into one instantly-saving Document Settings card (completed-by now a surgical PATCH), exact-fill demoted to a header badge + expandable details, template tab unchanged as the advanced path — delivered 2026-07-10
// pkg56 — "Email the firm on each submission" toggle strip (D-57a), instant like the acknowledgment toggle; default on — delivered 2026-07-09
// pkg55 — Re-extract available on HEALTHY exact-fill modules too: it was only offered as a repair when exact-fill was broken, leaving no path to rebuild a template that dropped content (the RAJ MEHTA signature block) — delivered 2026-07-09
// pkg53 — bulk owner controls (D-54a): "Set all questions to Attorney/Client/Module default" at page level and per section; auto questions are never touched; SectionEditors remount via a bulk-version key so page-level changes reach their local state — delivered 2026-07-09
// pkg52 — "AI Modules" renamed to "Document Modules" across the nav, page titles, back-links, and helper text (D-53b/c); routes unchanged — delivered 2026-07-09
// pkg46 — Signature style toggle on placeholder chips (per-occurrence: the same name can be plain on a printed-name line and script on the signature line — D-46b/c) — delivered 2026-07-05
// pkg43 — field ownership on review: module-level "Completed by" control (D-43f) + per-question owner selector (Client / Attorney / Auto from client record) with header badges — delivered 2026-07-04
// pkg42 — audience switch (Internal ↔ Client portal, D-41o): practice-area picker required for assembly modules going client-side; switching internal confirms + unassigns all clients and clears the acknowledgment toggle — delivered 2026-07-04
// pkg41 — "Requires client acknowledgment" toggle (assembly + client-audience modules only, D-41a); saved immediately on change — delivered 2026-07-04
// pkg40a — highlight-to-map made conspicuous: standing callout above the document + hover hint on paragraphs (replaces the easy-to-miss gray legend tip) — delivered 2026-07-04
// pkg40 — attorney-editable questions & placeholders: Add question / Add section on the Questionnaire tab; select text → "Turn into an answer" on the Document Template tab (captures source text so exact-fill keeps working); warning when token-edited placeholders lack source text — delivered 2026-07-04
// pkg39 — exact-fill: fidelity status strip (active / unavailable + failed-span list), re-extract button for pre-pkg39 Word modules; remap/edit preserve each placeholder's source text ("src") so template saves re-tag cleanly — delivered 2026-07-03
// pkg35 — assembly review: Document Template / Questionnaire / Preview tabs, placeholder remapping, flag resolution, block text editing, activation gate — delivered 2026-07-03
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import { moduleCompletedBy, fieldOwner, AUTO_RECORD_KEYS } from '../utils/fieldOwnership'; // pkg43

// Field type options and labels
const FIELD_TYPES = [
  { value: 'text',      label: 'Short text' },
  { value: 'multiline', label: 'Long text / paragraph' },
  { value: 'date',      label: 'Date' },
  { value: 'yes_no',    label: 'Yes / No' },
  { value: 'dropdown',  label: 'Dropdown (choose one)' },
];

// pkg35 — firm-variable labels (mirror of backend FIRM_VARIABLES). These
// auto-fill from the firm profile at generation time; clients never see them.
const FIRM_LABELS = {
  firm_name: 'Firm name', attorney_name: 'Attorney name', bar_number: 'Bar number',
  firm_phone: 'Firm phone', firm_fax: 'Firm fax', firm_email: 'Firm email',
  firm_website: 'Firm website', firm_address: 'Firm address', firm_city: 'Firm city',
  firm_state: 'Firm state', firm_zip: 'Firm ZIP',
};

// pkg35 — flatten the wizard schema into [{id, label, sectionTitle}] for the
// remap dropdowns, and an id → label lookup for chips.
function flattenFields(schema) {
  const out = [];
  for (const sec of (schema?.sections || [])) {
    for (const f of (sec.fields || [])) {
      out.push({ id: f.id, label: f.label, sectionTitle: sec.title });
    }
  }
  return out;
}

// pkg35 — serialize a block's runs to editable text with {{tokens}}, and back.
function runsToText(content) {
  return (content || []).map(r => {
    if (r.t === 'text')  return r.text;
    if (r.t === 'field') return `{{${r.id}}}`;
    if (r.t === 'firm')  return `{{firm:${r.key}}}`;
    return '';
  }).join('');
}
function textToRuns(text, fieldIds, originalContent) {
  // pkg39 — a text edit must not lose each placeholder's captured source text
  // ("src", used by the exact-fill tagger). Recreated field/firm runs consume
  // the original block's runs of the same id/key in order and inherit src.
  const srcPool = (originalContent || []).filter(r => r && r.t !== 'text');
  const takeSrc = (t, idOrKey) => {
    const i = srcPool.findIndex(r => r.t === t && (t === 'field' ? r.id === idOrKey : r.key === idOrKey));
    if (i === -1) return undefined;
    const src = srcPool[i].src;
    srcPool.splice(i, 1);
    return src;
  };

  const runs = [];
  const parts = String(text).split(/(\{\{[^}]+\}\})/g);
  for (const part of parts) {
    if (!part) continue;
    const m = part.match(/^\{\{([^}]+)\}\}$/);
    if (m) {
      const inner = m[1].trim();
      if (inner.startsWith('firm:')) {
        const key = inner.slice(5).trim();
        if (FIRM_LABELS[key]) {
          const fr = { t: 'firm', key };
          const src = takeSrc('firm', key);
          if (src) fr.src = src;
          runs.push(fr); continue;
        }
      } else if (fieldIds.has(inner)) {
        const fr = { t: 'field', id: inner };
        const src = takeSrc('field', inner);
        if (src) fr.src = src;
        runs.push(fr); continue;
      }
    }
    // Unknown token or plain text — keep literal, merging adjacent text runs.
    const last = runs[runs.length - 1];
    if (last && last.t === 'text') last.text += part;
    else runs.push({ t: 'text', text: part });
  }
  return runs.length ? runs : [{ t: 'text', text: '' }];
}

// ── Drag-to-reorder hook ─────────────────────────────────────────────────────
// Works on a flat array of items within a section. Returns drag event handlers.
function useDragReorder(items, setItems) {
  const dragIdx = useRef(null);
  const dragOverIdx = useRef(null);

  const onDragStart = useCallback((idx) => (e) => {
    dragIdx.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const onDragEnter = useCallback((idx) => (e) => {
    e.preventDefault();
    dragOverIdx.current = idx;
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const from = dragIdx.current;
    const to   = dragOverIdx.current;
    if (from === null || to === null || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    dragIdx.current = null;
    dragOverIdx.current = null;
  }, [items, setItems]);

  const onDragEnd = useCallback(() => {
    dragIdx.current = null;
    dragOverIdx.current = null;
  }, []);

  return { onDragStart, onDragEnter, onDragOver, onDrop, onDragEnd };
}

// ── Field editor row ─────────────────────────────────────────────────────────
function FieldRow({ field, idx, onUpdate, onDelete, dragHandlers, defaultExpanded, showOwnership, defaultOwner }) { // pkg43 — ownership selector
  const [expanded, setExpanded] = useState(!!defaultExpanded); // pkg40 — new questions open ready to edit

  function update(key, val) { onUpdate({ ...field, [key]: val }); }

  const rowStyle = {
    background: '#fff', border: '1px solid #dde3ec', borderRadius: 5,
    marginBottom: 6, overflow: 'hidden',
    cursor: 'default',
  };
  const headerStyle = {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
    background: '#f8fafb',
  };
  const inputStyle = {
    padding: '6px 9px', fontSize: 13, fontFamily: 'Arial, sans-serif',
    border: '1px solid #ccc', borderRadius: 4,
  };
  const labelStyle = {
    fontFamily: 'Arial, sans-serif', fontSize: 12, fontWeight: 600,
    color: '#444', display: 'block', marginBottom: 3,
  };

  return (
    <div
      style={rowStyle}
      draggable
      onDragStart={dragHandlers.onDragStart(idx)}
      onDragEnter={dragHandlers.onDragEnter(idx)}
      onDragOver={dragHandlers.onDragOver}
      onDrop={dragHandlers.onDrop}
      onDragEnd={dragHandlers.onDragEnd}
    >
      <div style={headerStyle}>
        {/* Drag handle */}
        <span
          title="Drag to reorder"
          style={{ cursor: 'grab', color: '#aaa', fontSize: 16, lineHeight: 1, userSelect: 'none', flexShrink: 0 }}
        >
          ⠿
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {field.label || <em style={{ color: '#aaa' }}>Unnamed field</em>}
          </div>
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888' }}>
            {FIELD_TYPES.find(t => t.value === field.type)?.label || field.type}
            {field.required ? ' · Required' : ''}
            {field.show_if ? ' · Conditional' : ''} {/* pkg35 — appears only when an earlier answer triggers it */}
            {showOwnership && ( /* pkg43 — who completes this field */
              <span style={{
                marginLeft: 6, padding: '1px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                background: fieldOwner(field, defaultOwner) === 'attorney' ? '#eef4ff' : fieldOwner(field, defaultOwner) === 'auto' ? '#f3eefe' : '#f0faf5',
                color:      fieldOwner(field, defaultOwner) === 'attorney' ? '#1a3a5c' : fieldOwner(field, defaultOwner) === 'auto' ? '#5b3a8e' : '#1a3a2a',
              }}>
                {fieldOwner(field, defaultOwner) === 'attorney' ? 'ATTORNEY' : fieldOwner(field, defaultOwner) === 'auto' ? 'AUTO' : 'CLIENT'}
              </span>
            )}
          </div>
        </div>

        <button
          onClick={() => setExpanded(x => !x)}
          style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', padding: '4px 10px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', flexShrink: 0 }}
        >
          {expanded ? 'Done' : 'Edit'}
        </button>
        <button
          onClick={() => onDelete(field.id)}
          title="Delete field"
          style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '4px 8px', borderRadius: 4, border: 'none', background: '#fef2f2', color: '#B85042', cursor: 'pointer', fontWeight: 700, flexShrink: 0 }}
        >
          ✕
        </button>
      </div>

      {expanded && (
        <div style={{ padding: '12px 14px', display: 'grid', gap: 12 }}>
          <div>
            <label style={labelStyle}>Field label</label>
            <input
              type="text"
              value={field.label}
              onChange={e => update('label', e.target.value)}
              style={{ ...inputStyle, width: '100%', maxWidth: 440, boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <label style={labelStyle}>Type</label>
              <select
                value={field.type}
                onChange={e => update('type', e.target.value)}
                style={inputStyle}
              >
                {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, paddingBottom: 2 }}>
              <input
                type="checkbox"
                id={`req-${field.id}`}
                checked={!!field.required}
                onChange={e => update('required', e.target.checked)}
                style={{ width: 15, height: 15, cursor: 'pointer' }}
              />
              <label htmlFor={`req-${field.id}`} style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>
                Required
              </label>
            </div>
          </div>

          {showOwnership && ( /* pkg43 — ownership selector */
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <label style={labelStyle}>Completed by</label>
                <select
                  value={field.filled_by || ''}
                  onChange={e => {
                    const v = e.target.value || null;
                    if (v === 'auto') {
                      onUpdate({ ...field, filled_by: 'auto', record_key: field.record_key || 'client_name' });
                    } else {
                      const next = { ...field, filled_by: v };
                      delete next.record_key;
                      if (!v) delete next.filled_by;
                      onUpdate(next);
                    }
                  }}
                  style={inputStyle}
                >
                  <option value="">Module default ({defaultOwner === 'attorney' ? 'Attorney' : 'Client'})</option>
                  <option value="client">Client</option>
                  <option value="attorney">Attorney</option>
                  <option value="auto">Auto — from client record</option>
                </select>
              </div>
              {field.filled_by === 'auto' && (
                <div>
                  <label style={labelStyle}>Client record value</label>
                  <select
                    value={field.record_key || 'client_name'}
                    onChange={e => update('record_key', e.target.value)}
                    style={inputStyle}
                  >
                    {AUTO_RECORD_KEYS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {field.type === 'dropdown' && (
            <div>
              <label style={labelStyle}>Options (one per line)</label>
              <textarea
                rows={4}
                value={(field.options || []).join('\n')}
                onChange={e => update('options', e.target.value.split('\n').map(o => o.trim()).filter(Boolean))}
                style={{ ...inputStyle, width: '100%', maxWidth: 400, resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>
          )}

          <div>
            <label style={labelStyle}>Placeholder hint (optional)</label>
            <input
              type="text"
              value={field.placeholder || ''}
              onChange={e => update('placeholder', e.target.value || null)}
              style={{ ...inputStyle, width: '100%', maxWidth: 440, boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <label style={labelStyle}>Instructions shown below field (optional)</label>
            <input
              type="text"
              value={field.instructions || ''}
              onChange={e => update('instructions', e.target.value || null)}
              style={{ ...inputStyle, width: '100%', maxWidth: 440, boxSizing: 'border-box' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section editor ───────────────────────────────────────────────────────────
function SectionEditor({ section, onUpdate, onDelete, makeFieldId, showOwnership, defaultOwner }) { // pkg43 — ownership props threaded to FieldRow
  const [fields, setFields] = useState(section.fields || []);

  // pkg53 (D-54a) — set every non-auto question in THIS section to one owner.
  // Auto questions keep their mapped source; 'default' clears the per-field
  // override so the module default governs again.
  function bulkOwner(owner) {
    const next = fields.map(f => {
      if (f.filled_by === 'auto') return f;
      if (owner === 'default') {
        const c = { ...f };
        delete c.filled_by;
        delete c.record_key;
        return c;
      }
      return { ...f, filled_by: owner };
    });
    setFields(next);
    onUpdate({ ...section, fields: next });
  }
  const newIds = useRef(new Set()); // pkg40 — freshly added questions open expanded
  const dragHandlers = useDragReorder(fields, (newFields) => {
    setFields(newFields);
    onUpdate({ ...section, fields: newFields });
  });

  function updateField(updatedField) {
    const next = fields.map(f => f.id === updatedField.id ? updatedField : f);
    setFields(next);
    onUpdate({ ...section, fields: next });
  }

  function deleteField(fieldId) {
    const next = fields.filter(f => f.id !== fieldId);
    setFields(next);
    onUpdate({ ...section, fields: next });
  }

  // pkg40 — Add question: appends a blank short-text field to this section,
  // opened for editing. Ids come from the page-level generator so they are
  // unique across the whole schema AND the template's placeholder references.
  function addField() {
    const f = { id: makeFieldId(), label: '', type: 'text', required: false, placeholder: null, instructions: null };
    newIds.current.add(f.id);
    const next = [...fields, f];
    setFields(next);
    onUpdate({ ...section, fields: next });
  }

  function updateTitle(val) {
    onUpdate({ ...section, title: val });
  }

  return (
    <div style={{ background: '#f8fafb', border: '1px solid #dde3ec', borderRadius: 6, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <input
          type="text"
          value={section.title}
          onChange={e => updateTitle(e.target.value)}
          style={{
            flex: 1, padding: '7px 10px', fontSize: 14, fontWeight: 700,
            fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4,
          }}
        />
        <button
          onClick={() => onDelete(section.id)}
          title="Delete section"
          style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', padding: '6px 10px', borderRadius: 4, border: 'none', background: '#fef2f2', color: '#B85042', cursor: 'pointer', fontWeight: 600 }}
        >
          Remove section
        </button>
      </div>

      {/* pkg53 (D-54a) — one-click owner for this section's questions */}
      {showOwnership && fields.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10, fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#666' }}>
          <span>Set this section's questions to:</span>
          {[['attorney', 'Attorney'], ['client', 'Client'], ['default', 'Module default']].map(([v, label]) => (
            <button
              key={v}
              onClick={() => bulkOwner(v)}
              style={{ fontSize: 11.5, fontFamily: 'Arial, sans-serif', padding: '3px 10px', borderRadius: 10, border: '1px solid #c9d4e3', background: '#fff', color: '#0F1838', cursor: 'pointer', fontWeight: 600 }}
            >
              {label}
            </button>
          ))}
          <span style={{ color: '#aaa' }}>· Auto questions are not changed</span>
        </div>
      )}

      {fields.length === 0 && (
        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#aaa', padding: '8px 0' }}>
          No fields — all fields in this section were deleted.
        </div>
      )}

      {fields.map((f, idx) => (
        <FieldRow
          key={f.id}
          field={f}
          idx={idx}
          onUpdate={updateField}
          onDelete={deleteField}
          dragHandlers={dragHandlers}
          defaultExpanded={newIds.current.has(f.id)}
          showOwnership={showOwnership}  /* pkg43 */
          defaultOwner={defaultOwner}    /* pkg43 */
        />
      ))}

      {/* pkg40 — Add question */}
      <button
        onClick={addField}
        style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '7px 14px', borderRadius: 4, border: '1px dashed #b7c4d6', background: '#fff', color: '#0F1838', cursor: 'pointer', fontWeight: 600, marginTop: 4 }}
      >
        + Add question
      </button>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// pkg35 — ASSEMBLY REVIEW (kind='assembly'): document template tab + preview
// ═════════════════════════════════════════════════════════════════════════════

// Chip styles: client placeholders read dark-on-light, firm variables read in
// the portal green (they auto-fill from the firm profile, D-34m).
const chipBase = {
  fontFamily: 'Arial, sans-serif', fontSize: 12, padding: '1px 8px',
  borderRadius: 4, whiteSpace: 'nowrap', cursor: 'pointer', display: 'inline-block',
};
const clientChipStyle = { ...chipBase, background: '#eef2f7', color: '#0F1838', border: '1px solid #c9d4e3' };
const firmChipStyle   = { ...chipBase, background: '#f0faf5', color: '#1e7e5e', border: '1px solid #b7e3cf' };
const flaggedChipStyle= { ...chipBase, background: '#fff8e1', color: '#7c5200', border: '1px solid #f5c842' };

function AssemblyTemplateTab({ schema, template, setTemplate, flags, setFlags, setSchema, makeFieldId, makeSectionId, exactFillActive }) {
  const fields   = flattenFields(schema);
  const fieldIds = new Set(fields.map(f => f.id));
  const labelOf  = (id) => fields.find(f => f.id === id)?.label || `(missing question ${id})`;

  const [sel, setSel]           = useState(null);  // { blockId, runIdx } — selected chip
  const [selRemap, setSelRemap] = useState('');
  const [editBlock, setEditBlock]   = useState(null); // block id being text-edited
  const [editText, setEditText]     = useState('');
  const [flagPick, setFlagPick]     = useState({});   // flagIdx → chosen field id

  // pkg40 — "Turn into an answer": text selected in a paragraph becomes a
  // placeholder. mk = { blockId, runIdx, start, end, text } for the selection;
  // the mk* form state drives the existing-question / new-question choice.
  const [mk, setMk]                     = useState(null);
  const [mkMode, setMkMode]             = useState('existing');
  const [mkExisting, setMkExisting]     = useState('');
  const [mkLabel, setMkLabel]           = useState('');
  const [mkType, setMkType]             = useState('text');
  const [mkSection, setMkSection]       = useState('');
  const [mkSectionTitle, setMkSectionTitle] = useState('');

  const flaggedPairs = new Set(
    (flags || []).filter(fl => fl.block && fl.field).map(fl => `${fl.block}:${fl.field}`)
  );

  // pkg40 (guardrail C) — on an exact-fill module, a placeholder with no
  // captured source text (typed in as a {{token}}) breaks tagging on the next
  // save and silently drops the module to rebuilt output. Count and warn.
  const missingSrcCount = exactFillActive
    ? (template.blocks || []).reduce((n, b) =>
        n + (b.content || []).filter(r => r && (r.t === 'field' || r.t === 'firm') && !r.src).length, 0)
    : 0;

  function mutateBlock(blockId, fn) {
    setTemplate(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => b.id === blockId ? fn(b) : b),
    }));
  }

  function selectChip(blockId, runIdx, run) {
    setMk(null); // pkg40 — one panel at a time
    setSel({ blockId, runIdx, run });
    setSelRemap(run.t === 'field' ? run.id : '');
  }

  // pkg46 (D-46b) — flip signature styling on THIS occurrence only. The
  // value fills in a script font (Segoe Script, italic) so a typed name
  // reads as a signature; other occurrences of the same question stay plain.
  function toggleSig() {
    if (!sel) return;
    const cur = sel.run.sig === true;
    mutateBlock(sel.blockId, b => ({
      ...b,
      content: b.content.map((r, i) => {
        if (i !== sel.runIdx) return r;
        const next = { ...r };
        if (cur) delete next.sig; else next.sig = true;
        return next;
      }),
    }));
    setSel(prev => prev ? { ...prev, run: { ...prev.run, sig: !cur } } : prev);
  }

  // pkg40 — mouseup on a paragraph: if the selection sits inside ONE static
  // text run of this block, open the Turn-into-an-answer panel. Selections
  // that cross a chip or another paragraph are ignored (v1 scope).
  function handleTextMouseUp(b) {
    const selObj = window.getSelection();
    if (!selObj || selObj.rangeCount === 0 || selObj.isCollapsed) return;
    const range = selObj.getRangeAt(0);
    if (range.startContainer !== range.endContainer) return;
    const node = range.startContainer;
    if (node.nodeType !== 3) return; // text node only
    const span = node.parentElement;
    if (!span || span.dataset === undefined || span.dataset.run === undefined) return;
    if (!span.closest('[data-block="' + b.id + '"]')) return;
    const runIdx = parseInt(span.dataset.run, 10);
    const run = (b.content || [])[runIdx];
    if (!run || run.t !== 'text') return;
    let s = Math.min(range.startOffset, range.endOffset);
    let e = Math.max(range.startOffset, range.endOffset);
    const rt = run.text || '';
    while (s < e && /\s/.test(rt[s])) s++;         // trim selection edges
    while (e > s && /\s/.test(rt[e - 1])) e--;
    if (e - s < 1) return;
    setSel(null);
    setMk({ blockId: b.id, runIdx, start: s, end: e, text: rt.slice(s, e) });
    setMkMode(fields.length ? 'existing' : 'new');
    setMkExisting(fields[0]?.id || '');
    setMkLabel(''); setMkType('text');
    setMkSection((schema.sections && schema.sections[0]?.id) || '__new__');
    setMkSectionTitle('');
  }

  // pkg40 — apply: split the text run around the selection and splice in a
  // field placeholder. The selected text is captured as the placeholder's
  // SOURCE TEXT, so re-tagging on save locates this exact spot in the
  // original .docx — exact-fill keeps working. Run emphasis (b/i/u) carries
  // onto the split text pieces.
  function applyMakeAnswer() {
    if (!mk) return;
    let fieldId = mkExisting;
    if (mkMode === 'new') {
      if (!mkLabel.trim()) return;
      if (mkSection === '__new__' && !mkSectionTitle.trim()) return;
      fieldId = makeFieldId();
      const newField = { id: fieldId, label: mkLabel.trim(), type: mkType, required: false, placeholder: null, instructions: null };
      if (mkSection === '__new__') {
        setSchema(prev => ({
          ...prev,
          sections: [...(prev.sections || []), { id: makeSectionId(), title: mkSectionTitle.trim(), fields: [newField] }],
        }));
      } else {
        setSchema(prev => ({
          ...prev,
          sections: (prev.sections || []).map(s => s.id === mkSection ? { ...s, fields: [...(s.fields || []), newField] } : s),
        }));
      }
    } else if (!fieldId) {
      return;
    }
    const { blockId, runIdx, start, end, text } = mk;
    mutateBlock(blockId, b => {
      const content = [];
      (b.content || []).forEach((r, i) => {
        if (i !== runIdx) { content.push(r); return; }
        const emph = {};
        if (r.b) emph.b = true;
        if (r.i) emph.i = true;
        if (r.u) emph.u = true;
        const before = (r.text || '').slice(0, start);
        const after  = (r.text || '').slice(end);
        if (before) content.push({ t: 'text', text: before, ...emph });
        content.push({ t: 'field', id: fieldId, src: text });
        if (after) content.push({ t: 'text', text: after, ...emph });
      });
      return { ...b, content };
    });
    setMk(null);
  }

  function applyRemap() {
    if (!sel || !selRemap) return;
    mutateBlock(sel.blockId, b => ({
      ...b,
      // pkg39 — remapping changes WHICH question fills the spot; the spot's
      // source text in the original document is unchanged, so src carries over.
      content: b.content.map((r, i) => i === sel.runIdx
        ? (sel.run.src ? { t: 'field', id: selRemap, src: sel.run.src } : { t: 'field', id: selRemap })
        : r),
    }));
    setSel(null);
  }

  function removePlaceholder() {
    if (!sel) return;
    mutateBlock(sel.blockId, b => ({
      ...b,
      content: b.content.map((r, i) => i === sel.runIdx ? { t: 'text', text: '__________' } : r),
    }));
    setSel(null);
  }

  function startEditBlock(b) {
    setSel(null);
    setEditBlock(b.id);
    setEditText(runsToText(b.content));
  }

  function saveEditBlock() {
    mutateBlock(editBlock, b => ({ ...b, content: textToRuns(editText, fieldIds, b.content) })); // pkg39 — b.content carries src over
    setEditBlock(null); setEditText('');
  }

  // Flag resolution: "fix" remaps every run of the flagged field inside the
  // flagged block to the chosen question; "keep" just dismisses the flag.
  function resolveFlag(idx, fix) {
    const fl = flags[idx];
    if (fix && fl.block && fl.field) {
      const to = flagPick[idx] || fl.field;
      mutateBlock(fl.block, b => ({
        ...b,
        // pkg39 — keep r.src through the remap (exact-fill needs it)
        content: b.content.map(r => (r.t === 'field' && r.id === fl.field)
          ? (r.src ? { t: 'field', id: to, src: r.src } : { t: 'field', id: to })
          : r),
      }));
    }
    setFlags(prev => prev.filter((_, i) => i !== idx));
  }

  return (
    <div>
      {/* Unresolved flags */}
      {(flags || []).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {flags.map((fl, idx) => (
            <div key={idx} style={{ background: '#fffdf5', border: '1px solid #f5c842', borderRadius: 6, padding: '12px 14px', marginBottom: 8 }}>
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 700, color: '#7c5200', marginBottom: 4 }}>
                Check this mapping
              </div>
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginBottom: 10 }}>{fl.reason}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {fl.block && fl.field && (
                  <select
                    value={flagPick[idx] || fl.field}
                    onChange={e => setFlagPick(prev => ({ ...prev, [idx]: e.target.value }))}
                    style={{ padding: '6px 8px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4, maxWidth: 340 }}
                  >
                    {fields.map(f => <option key={f.id} value={f.id}>{f.sectionTitle} — {f.label}</option>)}
                  </select>
                )}
                {fl.block && fl.field && (
                  <button
                    onClick={() => resolveFlag(idx, true)}
                    style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '6px 14px', borderRadius: 4, border: 'none', background: '#0F1838', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Fix mapping
                  </button>
                )}
                <button
                  onClick={() => resolveFlag(idx, false)}
                  style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '6px 14px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', color: '#555', cursor: 'pointer' }}
                >
                  Keep as is
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 10, fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#555', flexWrap: 'wrap' }}>
        <span><span style={{ ...clientChipStyle, cursor: 'default' }}>Client answer</span> — filled from the questionnaire</span>
        <span><span style={{ ...firmChipStyle, cursor: 'default' }}>Firm data</span> — auto-fills from Firm Settings</span>
      </div>

      {/* pkg40a — the highlight-to-map feature is the recovery path whenever
          extraction missed a blank; it has to be impossible to overlook. */}
      <div style={{ background: '#f0faf5', border: '1px solid #4caf7d', borderRadius: 6, padding: '10px 14px', marginBottom: 10, fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#1a3a2a' }}>
        <strong>Something not fillable that should be?</strong> Highlight the text in the document below —
        a dollar amount, a name, a date — and choose <strong>"Turn into an answer."</strong> It becomes a blank
        filled by a question of your choice, new or existing.
      </div>

      {/* pkg40 (guardrail C) — token-typed placeholders with no source text
          will break exact-fill tagging on the next save. */}
      {missingSrcCount > 0 && (
        <div style={{ background: '#fff8e1', border: '1px solid #f5c842', borderRadius: 6, padding: '10px 14px', marginBottom: 10, fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#7c5200' }}>
          {missingSrcCount} placeholder{missingSrcCount === 1 ? '' : 's'} here {missingSrcCount === 1 ? 'has' : 'have'} no captured source text
          (usually from typing a {'{{token}}'} by hand). Saving will drop this module to <strong>rebuilt output</strong> until
          {missingSrcCount === 1 ? ' it is' : ' they are'} re-created by highlighting the document text and choosing "Turn into an answer."
        </div>
      )}

      {/* Document */}
      <div style={{ background: '#fff', border: '1px solid #dde3ec', borderRadius: 6, padding: '20px 24px' }}>
        {(template.blocks || []).map(b => (
          <div key={b.id} style={{ marginBottom: 12 }}>
            {b.show_if && (
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#7c5200', marginBottom: 2 }}>
                Only appears when: {labelOf(b.show_if.field)} = {String(b.show_if.equals)}
              </div>
            )}
            {editBlock === b.id ? (
              <div>
                <textarea
                  rows={4}
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, fontFamily: 'Georgia, serif', border: '1px solid #0F1838', borderRadius: 4, resize: 'vertical' }}
                />
                <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', margin: '4px 0 6px' }}>
                  Placeholders appear as tokens — keep them intact to keep the mapping. Tokens in this paragraph:{' '}
                  {(b.content || []).filter(r => r.t !== 'text').map((r, i) => (
                    <code key={i} style={{ background: '#f5f5f5', padding: '0 4px', borderRadius: 3, marginRight: 6 }}>
                      {r.t === 'field' ? `{{${r.id}}} = ${labelOf(r.id)}` : `{{firm:${r.key}}} = ${FIRM_LABELS[r.key]}`}
                    </code>
                  ))}
                  {!(b.content || []).some(r => r.t !== 'text') && 'none'}
                  {' '}To add a NEW blank, save this text first, then highlight the text to replace and choose "Turn into an answer" — that keeps exact-fill working. {/* pkg40 */}
                </div>
                <button onClick={saveEditBlock} style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', padding: '5px 12px', borderRadius: 4, border: 'none', background: '#0F1838', color: '#fff', fontWeight: 600, cursor: 'pointer', marginRight: 6 }}>Save text</button>
                <button onClick={() => setEditBlock(null)} style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', padding: '5px 12px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', color: '#555', cursor: 'pointer' }}>Cancel</button>
              </div>
            ) : (
              <div
                data-block={b.id}
                onMouseUp={() => handleTextMouseUp(b)}
                title='Highlight text to turn it into an answer' /* pkg40a */
                style={{
                  fontFamily: 'Georgia, serif', fontSize: 14, lineHeight: 1.9, color: '#0F1838',
                  fontWeight: b.kind === 'heading' ? 700 : 400,
                  textAlign: b.align === 'center' ? 'center' : 'left',
                  position: 'relative', paddingRight: 44,
                  cursor: 'text', /* pkg40a — signals the text is selectable */
                }}
              >
                {(b.content || []).map((r, i) => {
                  // pkg40 — data-run anchors text selection for "Turn into an answer"
                  if (r.t === 'text') return <span key={i} data-run={i}>{r.text}</span>;
                  if (r.t === 'firm') {
                    return (
                      <span key={i} style={firmChipStyle} onClick={() => selectChip(b.id, i, r)} title="Auto-fills from Firm Settings">
                        {FIRM_LABELS[r.key] || r.key}
                      </span>
                    );
                  }
                  const isFlagged = flaggedPairs.has(`${b.id}:${r.id}`);
                  return (
                    <span key={i} style={isFlagged ? flaggedChipStyle : clientChipStyle} onClick={() => selectChip(b.id, i, r)} title={isFlagged ? 'This mapping is flagged — see above' : 'Click to change which question fills this'}>
                      {isFlagged ? '⚠ ' : ''}{labelOf(r.id)}
                    </span>
                  );
                })}
                <button
                  onClick={() => startEditBlock(b)}
                  title="Edit this paragraph's text"
                  style={{ position: 'absolute', top: 0, right: 0, fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 4, border: '1px solid #ddd', background: '#fafafa', color: '#888', cursor: 'pointer' }}
                >
                  Edit
                </button>
              </div>
            )}

            {/* pkg40 — Turn-into-an-answer panel, rendered under its block */}
            {mk && mk.blockId === b.id && (
              <div style={{ background: '#f8fafb', border: '1px solid #b7c4d6', borderRadius: 6, padding: '12px 14px', marginTop: 8 }}>
                <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                  Turn "{mk.text.length > 60 ? mk.text.slice(0, 60) + '…' : mk.text}" into an answer
                </div>
                <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#555', marginBottom: 10 }}>
                  This text will be replaced by the answer in every generated document.
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'Arial, sans-serif', fontSize: 13, marginBottom: 6, cursor: fields.length ? 'pointer' : 'default', opacity: fields.length ? 1 : 0.5 }}>
                  <input type="radio" name={`mkmode-${b.id}`} checked={mkMode === 'existing'} disabled={!fields.length} onChange={() => setMkMode('existing')} style={{ width: 14, height: 14 }} />
                  Fill with an existing question
                </label>
                {mkMode === 'existing' && (
                  <select
                    value={mkExisting}
                    onChange={e => setMkExisting(e.target.value)}
                    style={{ display: 'block', margin: '0 0 10px 22px', padding: '6px 8px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4, maxWidth: 380 }}
                  >
                    {fields.map(f => <option key={f.id} value={f.id}>{f.sectionTitle} — {f.label}</option>)}
                  </select>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'Arial, sans-serif', fontSize: 13, marginBottom: 6, cursor: 'pointer' }}>
                  <input type="radio" name={`mkmode-${b.id}`} checked={mkMode === 'new'} onChange={() => setMkMode('new')} style={{ width: 14, height: 14 }} />
                  Create a new question
                </label>
                {mkMode === 'new' && (
                  <div style={{ margin: '0 0 10px 22px', display: 'grid', gap: 6, maxWidth: 380 }}>
                    <input
                      type="text"
                      placeholder="What should the client be asked?"
                      value={mkLabel}
                      onChange={e => setMkLabel(e.target.value)}
                      style={{ padding: '6px 9px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4 }}
                    />
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <select value={mkType} onChange={e => setMkType(e.target.value)} style={{ padding: '6px 8px', fontSize: 12, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4 }}>
                        {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <select value={mkSection} onChange={e => setMkSection(e.target.value)} style={{ padding: '6px 8px', fontSize: 12, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4 }}>
                        {(schema.sections || []).map(s => <option key={s.id} value={s.id}>Section: {s.title}</option>)}
                        <option value="__new__">+ New section…</option>
                      </select>
                    </div>
                    {mkSection === '__new__' && (
                      <input
                        type="text"
                        placeholder="New section title"
                        value={mkSectionTitle}
                        onChange={e => setMkSectionTitle(e.target.value)}
                        style={{ padding: '6px 9px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4 }}
                      />
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button
                    onClick={applyMakeAnswer}
                    disabled={mkMode === 'new' ? (!mkLabel.trim() || (mkSection === '__new__' && !mkSectionTitle.trim())) : !mkExisting}
                    style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '6px 14px', borderRadius: 4, border: 'none', background: '#0F1838', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: (mkMode === 'new' ? (!mkLabel.trim() || (mkSection === '__new__' && !mkSectionTitle.trim())) : !mkExisting) ? 0.5 : 1 }}
                  >
                    Turn into answer
                  </button>
                  <button onClick={() => setMk(null)} style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '6px 14px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', color: '#555', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Selected-chip panel, rendered directly under its block */}
            {sel && sel.blockId === b.id && (
              <div style={{ background: '#f8fafb', border: '1px solid #dde3ec', borderRadius: 6, padding: '12px 14px', marginTop: 8 }}>
                {sel.run.t === 'field' ? (
                  <>
                    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                      This blank is filled by: <span style={{ fontWeight: 400 }}>{labelOf(sel.run.id)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <select
                        value={selRemap}
                        onChange={e => setSelRemap(e.target.value)}
                        style={{ padding: '6px 8px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4, maxWidth: 340 }}
                      >
                        {fields.map(f => <option key={f.id} value={f.id}>{f.sectionTitle} — {f.label}</option>)}
                      </select>
                      <button onClick={applyRemap} style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '6px 14px', borderRadius: 4, border: 'none', background: '#0F1838', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Change question</button>
                      <button onClick={toggleSig} style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '6px 14px', borderRadius: 4, border: sel.run.sig ? 'none' : '1px solid #9db8e8', background: sel.run.sig ? '#eef4ff' : '#fff', color: '#1a3a5c', fontWeight: 600, cursor: 'pointer' }} title="Fills in a script font so the typed name reads as a signature — this spot only">{sel.run.sig ? '✒ Signature style: ON' : '✒ Signature style'}</button>
                      <button onClick={removePlaceholder} style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '6px 14px', borderRadius: 4, border: 'none', background: '#fef2f2', color: '#B85042', fontWeight: 600, cursor: 'pointer' }}>Remove (manual blank)</button>
                      <button onClick={() => setSel(null)} style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '6px 14px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', color: '#555', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, marginBottom: 8 }}>
                      <strong>{FIRM_LABELS[sel.run.key] || sel.run.key}</strong> auto-fills from your Firm Settings when a document is generated. Clients are never asked for it, and if another firm ever adopts this module, <em>their</em> firm data fills in — never yours.
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button onClick={toggleSig} style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '6px 14px', borderRadius: 4, border: sel.run.sig ? 'none' : '1px solid #9db8e8', background: sel.run.sig ? '#eef4ff' : '#fff', color: '#1a3a5c', fontWeight: 600, cursor: 'pointer' }} title="Fills in a script font so the typed name reads as a signature — this spot only">{sel.run.sig ? '✒ Signature style: ON' : '✒ Signature style'}</button>
                      <button onClick={removePlaceholder} style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '6px 14px', borderRadius: 4, border: 'none', background: '#fef2f2', color: '#B85042', fontWeight: 600, cursor: 'pointer' }}>Remove (manual blank)</button>
                      <button onClick={() => setSel(null)} style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '6px 14px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', color: '#555', cursor: 'pointer' }}>Close</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── pkg59 (D-58a/b) — Signatures panel ───────────────────────────────────────
// Detection is a client-side LENS over the template: no AI call, no stored
// state, recomputed every render. A run is a signature candidate when it is
// (a) already script-styled (run.sig), (b) the firm's attorney_name inside a
// signature-looking block, (c) an auto field fed by the Sign-for-the-firm
// act, or (d) a field whose label or surrounding block text reads like a
// signature line. Panel edits write the SAME data the template chips write:
// run.sig per occurrence, and remaps that carry src (exact-fill safe). A
// remap here keeps the occurrence's sig flag — changing WHO signs shouldn't
// silently unstyle the line.
const SIG_CONTEXT_RE = /_{3,}|signature|\bby:|\bsign(ed|er)?\b|\bwitness\b|\bnotary\b/i;
const SIG_PRINT_RE   = /\bprint/i;
const SIG_DATE_AUTO  = new Set(['current_day', 'current_month', 'current_year', 'current_date']);

function detectSignatureLines(template, schema) {
  const fieldById = {};
  for (const sec of (schema?.sections || [])) {
    for (const f of (sec.fields || [])) fieldById[f.id] = f;
  }
  const rows = [];
  for (const b of (template?.blocks || [])) {
    const content = b.content || [];
    const blockText = content.map(r => (r && r.t === 'text' ? (r.text || '') : ' ')).join('');
    const sigContext = SIG_CONTEXT_RE.test(blockText);

    // Date behavior for this block (shown on every signature row in it).
    let dateInfo = null;
    for (const r of content) {
      if (r && r.t === 'field') {
        const f = fieldById[r.id];
        if (f && f.filled_by === 'auto' && SIG_DATE_AUTO.has(f.record_key)) {
          dateInfo = 'Fills automatically when the document is generated';
          break;
        }
        if (f && f.type === 'date') dateInfo = `Fills from the question "${f.label}"`;
      }
    }
    if (!dateInfo && /\bdate\b/i.test(blockText) && /_{3,}/.test(blockText)) {
      dateInfo = 'Manual blank — not mapped to anything';
    }

    content.forEach((r, i) => {
      if (!r || r.t === 'text') return;
      const f = r.t === 'field' ? fieldById[r.id] : null;
      const isFirmAttorney = r.t === 'firm' && r.key === 'attorney_name';
      const isSignerAct    = !!(f && f.filled_by === 'auto' && f.record_key === 'attorney_signature_name');
      const labelSig       = !!(f && /sign/i.test(f.label || ''));
      const nameInContext  = !!(f && sigContext && /name/i.test(f.label || ''));

      const isCandidate =
        r.sig === true ||
        (isFirmAttorney && sigContext) ||   // context filter keeps letterhead out
        isSignerAct ||
        labelSig ||
        nameInContext;
      if (!isCandidate) return;

      let title, signedBy;
      if (isFirmAttorney) {
        title = 'Attorney signature';
        signedBy = { kind: 'fixed', text: 'Attorney — from the firm record (Prepare picks the signing attorney)' };
      } else if (isSignerAct) {
        title = 'Firm signer';
        signedBy = { kind: 'fixed', text: 'Fills when the attorney signs for the firm at Prepare — blank until then' };
      } else if (f) {
        title = SIG_PRINT_RE.test(blockText)
          ? 'Printed name'
          : (/signature/i.test(f.label || '') ? f.label : 'Signature line');
        signedBy = { kind: 'field', id: r.id };
      } else {
        title = 'Signature line';
        signedBy = { kind: 'fixed', text: FIRM_LABELS[r.key] || r.key };
      }

      // Snippet: the block's text with blanks shown, trimmed around this run.
      let before = '';
      for (let k = 0; k < i; k++) {
        const rk = content[k];
        before += rk && rk.t === 'text' ? (rk.text || '') : ' ______ ';
      }
      let after = '';
      for (let k = i + 1; k < content.length; k++) {
        const rk = content[k];
        after += rk && rk.t === 'text' ? (rk.text || '') : ' ______ ';
      }
      const snippet =
        (before.length > 44 ? '…' + before.slice(-44) : before) +
        ' ______ ' +
        (after.length > 44 ? after.slice(0, 44) + '…' : after);

      rows.push({ blockId: b.id, runIdx: i, run: r, title, signedBy, dateInfo, snippet: snippet.trim() });
    });
  }
  return rows;
}

function SignaturesPanel({ schema, template, setTemplate, setTab }) {
  const fields = flattenFields(schema);
  const rows = detectSignatureLines(template, schema);

  function mutateRun(blockId, runIdx, fn) {
    setTemplate(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => b.id !== blockId ? b : ({
        ...b,
        content: b.content.map((r, i) => i === runIdx ? fn(r) : r),
      })),
    }));
  }

  // pkg59 — remap preserves src (exact-fill critical) AND the occurrence's sig.
  function remapRun(blockId, runIdx, newId) {
    mutateRun(blockId, runIdx, r => {
      const next = { t: 'field', id: newId };
      if (r.src) next.src = r.src;
      if (r.sig === true) next.sig = true;
      return next;
    });
  }

  function toggleSigRun(blockId, runIdx) {
    mutateRun(blockId, runIdx, r => {
      const next = { ...r };
      if (next.sig === true) delete next.sig; else next.sig = true;
      return next;
    });
  }

  function showInDoc(blockId) {
    setTab('template');
    setTimeout(() => {
      const el = document.querySelector(`[data-block="${blockId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'background 0.4s';
        el.style.background = '#fff8e1';
        setTimeout(() => { el.style.background = ''; }, 1600);
      }
    }, 150);
  }

  const label13 = { fontFamily: 'Arial, sans-serif', fontSize: 13 };
  const dim12   = { fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888' };

  return (
    <div style={{ background: '#fff', border: '2px solid #9db8e8', borderRadius: 6, marginBottom: 14 }}>
      <div style={{ padding: '11px 16px', borderBottom: '1px solid #e5ecf7', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ ...label13, fontWeight: 700, fontSize: 14, color: '#1a3a5c' }}>✒ Signatures</span>
        <span style={dim12}>
          {rows.length
            ? `${rows.length} signature line${rows.length === 1 ? '' : 's'} found in this document`
            : 'No signature lines detected in this document'}
        </span>
      </div>

      {rows.map((row, idx) => (
        <div key={`${row.blockId}-${row.runIdx}`} style={{ padding: '12px 16px', borderTop: idx === 0 ? 'none' : '1px solid #eef1f5' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...label13, fontWeight: 700 }}>{row.title}</div>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: 12, color: '#888', marginTop: 2 }}>
                “{row.snippet}”
              </div>
            </div>
            <button
              onClick={() => showInDoc(row.blockId)}
              style={{ ...dim12, background: 'none', border: 'none', color: '#0F1838', cursor: 'pointer', textDecoration: 'underline', padding: 0, whiteSpace: 'nowrap' }}
            >
              Show in document
            </button>
          </div>
          <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
            <span style={{ ...label13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#888', fontSize: 12 }}>Signed by</span>
              {row.signedBy.kind === 'field' ? (
                <select
                  value={row.signedBy.id}
                  onChange={e => remapRun(row.blockId, row.runIdx, e.target.value)}
                  style={{ padding: '4px 7px', fontSize: 12, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4, maxWidth: 260 }}
                >
                  {fields.map(f => <option key={f.id} value={f.id}>{f.sectionTitle} — {f.label}</option>)}
                </select>
              ) : (
                <span style={{ fontSize: 12 }}>{row.signedBy.text}</span>
              )}
            </span>
            <button
              onClick={() => toggleSigRun(row.blockId, row.runIdx)}
              title="Fills in a script font so the typed name reads as a signature — this line only"
              style={{
                fontSize: 12, fontFamily: 'Arial, sans-serif', padding: '4px 12px', borderRadius: 10, cursor: 'pointer', fontWeight: 600,
                border: row.run.sig ? 'none' : '1px solid #c9d4e3',
                background: row.run.sig ? '#eef4ff' : '#fff',
                color: row.run.sig ? '#1a3a5c' : '#555',
                fontStyle: row.run.sig ? 'italic' : 'normal',
              }}
            >
              {row.run.sig ? '✒ Script style: ON' : 'Script style: off — plain text'}
            </button>
            {row.dateInfo && (
              <span style={dim12}>
                <span style={{ color: '#aaa' }}>Date:</span> {row.dateInfo}
              </span>
            )}
          </div>
        </div>
      ))}

      <div style={{ padding: '9px 16px', borderTop: '1px solid #e5ecf7', background: '#f8fafb', borderRadius: '0 0 4px 4px', ...dim12 }}>
        Missed a signature line? Open the <strong>Document Template</strong> tab and click any blank — it appears here.
        Signature changes apply with <strong>Save Changes</strong>.
      </div>
    </div>
  );
}

function AssemblyPreviewTab({ schema, template }) {
  const fields  = flattenFields(schema);
  const labelOf = (id) => fields.find(f => f.id === id)?.label || id;

  return (
    <div>
      <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginTop: 0 }}>
        How the generated document reads. Client answers appear as gray sample values, firm data in green.
        Conditional clauses are tagged — they only appear in a real document when the answer triggers them.
      </p>
      <div style={{ background: '#fff', border: '1px solid #dde3ec', borderRadius: 6, padding: '24px 28px' }}>
        {(template.blocks || []).map(b => (
          <div key={b.id} style={{ marginBottom: 12 }}>
            {b.show_if && (
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#7c5200', marginBottom: 2 }}>
                Conditional — only when: {labelOf(b.show_if.field)} = {String(b.show_if.equals)}
              </div>
            )}
            <div style={{
              fontFamily: 'Georgia, serif', fontSize: 14, lineHeight: 1.9, color: '#0F1838',
              fontWeight: b.kind === 'heading' ? 700 : 400,
              textAlign: b.align === 'center' ? 'center' : 'left',
            }}>
              {(b.content || []).map((r, i) => {
                if (r.t === 'text')  return <span key={i}>{r.text}</span>;
                if (r.t === 'firm')  return <span key={i} style={{ color: '#1e7e5e', fontStyle: 'italic' }}>[{FIRM_LABELS[r.key] || r.key}]</span>;
                return <span key={i} style={{ color: '#888', fontStyle: 'italic' }}>[{labelOf(r.id)}]</span>;
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── pkg42 — audience switch (D-41o) ──────────────────────────────────────────
// Both directions, with the guards the upload path enforces:
//   internal → client: assembly modules must carry a practice area — a picker
//     appears when one isn't set yet; the PATCH sends both fields together.
//   client → internal: confirms first (it unassigns every client so the
//     module vanishes from their portals) — the backend also clears the
//     acknowledgment toggle and any unsigned pending snapshots. Signed
//     acknowledgment history is never touched.
function AudienceStrip({ mod, onChanged, onError }) {
  const [busy, setBusy] = useState(false);
  const [pickingPA, setPickingPA] = useState(false);
  const [areas, setAreas] = useState([]);
  const [paId, setPaId] = useState('');

  const isClient = mod.audience === 'client';

  async function switchToInternal() {
    const n = mod.client_count || 0;
    const ok = window.confirm(
      'Move this module to internal (staff-only)?\n\n' +
      (n ? `• ${n} assigned client${n === 1 ? '' : 's'} will be unassigned and it disappears from their portal${n === 1 ? '' : 's'}.\n` : '') +
      '• The client-acknowledgment setting is turned off.\n' +
      '• Signed acknowledgment records are kept.\n' +
      '• Staff fill it from the Document Modules list; existing submissions stay.'
    );
    if (!ok) return;
    setBusy(true);
    try {
      const updated = await adminApi.updateFirmModule(mod.id, { audience: 'internal' });
      onChanged(updated);
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function startSwitchToClient() {
    // Assembly modules need a practice area on the client side. If one is
    // already set (e.g. it was client-audience before), switch directly.
    if (mod.kind === 'assembly' && !mod.practice_area_id) {
      setBusy(true);
      try {
        const list = await adminApi.getModulePracticeAreas();
        setAreas(list);
        setPickingPA(true);
      } catch (err) {
        onError(err.message);
      } finally {
        setBusy(false);
      }
      return;
    }
    await doSwitchToClient(null);
  }

  async function doSwitchToClient(practiceAreaId) {
    setBusy(true);
    try {
      const payload = { audience: 'client' };
      if (practiceAreaId) payload.practice_area_id = practiceAreaId;
      const updated = await adminApi.updateFirmModule(mod.id, payload);
      setPickingPA(false);
      onChanged(updated);
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{
      padding: '10px 16px', borderTop: '1px solid #eef1f5', /* pkg59 — row inside the Document Settings card */
      fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555',
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <span>
        <strong>Audience:</strong>{' '}
        {isClient ? 'Client portal — assigned clients fill it out' : 'Internal — staff fill it out in the admin portal'}
      </span>
      {!pickingPA && (
        <button
          onClick={isClient ? switchToInternal : startSwitchToClient}
          disabled={busy}
          style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', padding: '5px 12px', borderRadius: 4, border: 'none', background: '#eef2f7', color: '#0F1838', fontWeight: 600, cursor: 'pointer' }}
        >
          {busy ? 'Working…' : isClient ? 'Switch to internal' : 'Switch to client portal'}
        </button>
      )}
      {pickingPA && (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select
            value={paId}
            onChange={e => setPaId(e.target.value)}
            style={{ padding: '5px 8px', fontSize: 12, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4 }}
          >
            <option value="">— Pick a practice area —</option>
            {areas.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
          <button
            onClick={() => paId && doSwitchToClient(paId)}
            disabled={busy || !paId}
            style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', padding: '5px 12px', borderRadius: 4, border: 'none', background: '#0F1838', color: '#fff', fontWeight: 600, cursor: paId ? 'pointer' : 'default', opacity: paId ? 1 : 0.5 }}
          >
            {busy ? 'Working…' : 'Move to client portal'}
          </button>
          <button
            onClick={() => setPickingPA(false)}
            style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', padding: '5px 10px', borderRadius: 4, border: 'none', background: 'none', color: '#888', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Cancel
          </button>
        </span>
      )}
    </div>
  );
}

// ── pkg43 — completed-by control (D-43f) ─────────────────────────────────────
// The module-level default. Changing it re-defaults NOTHING hand-set: it only
// changes what fields WITHOUT an explicit per-question setting assume.
// pkg59 (D-58c) — now saves INSTANTLY via the surgical completed_by PATCH
// (stored schema only — unsaved local edits stay local), matching every other
// row in the Document Settings card. onSaved patches the page's local schema
// so ownership defaults recompute immediately.
function CompletedByStrip({ mod, schema, onSaved, onError }) {
  const [busy, setBusy] = useState(false);
  if (mod.kind !== 'assembly' || mod.audience !== 'client') return null;
  const cb = moduleCompletedBy(schema);
  const labels = { client: 'Client', attorney: 'Attorney', both: 'Both' };

  async function change(v) {
    setBusy(true);
    try {
      const updated = await adminApi.updateFirmModule(mod.id, { completed_by: v });
      onSaved(updated, v);
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{
      padding: '10px 16px', borderTop: '1px solid #eef1f5', /* pkg59 — row inside the Document Settings card */
      fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555',
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    }}>
      <span>
        <strong>Completed by:</strong>{' '}
        {cb === 'attorney'
          ? "Attorney — you prepare each client's document (Prepare on the modules list); the client only reviews"
          : cb === 'both'
            ? "Both — questions are split; set each question's owner on the Questionnaire tab"
            : 'Client — the client answers everything'}
      </span>
      <select
        value={cb}
        disabled={busy}
        onChange={e => change(e.target.value)}
        style={{ padding: '5px 8px', fontSize: 12, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4 }}
      >
        {Object.entries(labels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <span style={{ fontSize: 11, color: '#999', flexBasis: '100%' }}>
        Only affects questions still on "Module default" — per-question settings you've made are kept.
        {busy && <em style={{ marginLeft: 6, color: '#888' }}>Saving…</em>}
      </span>
    </div>
  );
}

// ── pkg41 — acknowledgment toggle (D-41a) ────────────────────────────────────
// Client-portal assembly modules only: the toggle saves immediately (its own
// PATCH), independent of the Save Changes button, so flipping it never drags
// unsaved template edits along. When ON, clients must review the rendered
// document in-portal after submitting and sign with a typed name; the signed
// copy is frozen exactly as shown.
function AckToggleStrip({ mod, onToggled, onError }) {
  const [busy, setBusy] = useState(false);
  if (mod.kind !== 'assembly' || mod.audience !== 'client') return null;

  async function toggle() {
    setBusy(true);
    try {
      const updated = await adminApi.updateFirmModule(mod.id, {
        requires_acknowledgment: !mod.requires_acknowledgment,
      });
      onToggled(updated);
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const on = mod.requires_acknowledgment === true;
  return (
    <div style={{
      padding: '10px 16px', borderTop: '1px solid #eef1f5', /* pkg59 — row inside the Document Settings card */
      background: on ? '#f6f9ff' : 'transparent',
      fontFamily: 'Arial, sans-serif', fontSize: 13, color: on ? '#1a3a5c' : '#555',
      display: 'flex', alignItems: 'flex-start', gap: 10,
    }}>
      <input
        type="checkbox"
        checked={on}
        disabled={busy}
        onChange={toggle}
        style={{ width: 16, height: 16, marginTop: 1, cursor: busy ? 'default' : 'pointer' }}
        id="ack-toggle"
      />
      <label htmlFor="ack-toggle" style={{ cursor: busy ? 'default' : 'pointer' }}>
        <strong>Requires client acknowledgment.</strong>{' '}
        After submitting, the client reviews the completed document in their portal and signs it
        with a typed name. An exact copy of the document as signed is preserved, with the signer's
        name, time, and IP address. You can download the signed copy and a Certificate of
        Acknowledgment from the submissions list.
        {busy && <em style={{ marginLeft: 6, color: '#888' }}>Saving…</em>}
      </label>
    </div>
  );
}

// ── pkg56 (D-57a) — per-module submission-email toggle ──────────────────────
function NotifyToggleStrip({ mod, onToggled, onError }) {
  const [busy, setBusy] = useState(false);
  if (mod.kind !== 'assembly' || mod.audience !== 'client') return null;

  async function toggle() {
    setBusy(true);
    try {
      const updated = await adminApi.updateFirmModule(mod.id, {
        notify_on_submit: mod.notify_on_submit === false, // flip
      });
      onToggled(updated);
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const on = mod.notify_on_submit !== false;
  return (
    <div style={{
      padding: '10px 16px', borderTop: '1px solid #eef1f5', /* pkg59 — row inside the Document Settings card */
      fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555',
      display: 'flex', alignItems: 'flex-start', gap: 10,
    }}>
      <input
        type="checkbox"
        checked={on}
        disabled={busy}
        onChange={toggle}
        style={{ width: 16, height: 16, marginTop: 1, cursor: busy ? 'default' : 'pointer' }}
        id="notify-toggle"
      />
      <label htmlFor="notify-toggle" style={{ cursor: busy ? 'default' : 'pointer' }}>
        <strong>Email the firm when this is first submitted.</strong>{' '}
        One email per client, with the filled document attached — a client editing and
        re-submitting never re-sends it. The signing notice (with the signed copy and
        certificate) is separate and always sends.
        {busy && <em style={{ marginLeft: 6, color: '#888' }}>Saving…</em>}
      </label>
    </div>
  );
}

// ── pkg39 — exact-fill fidelity status strip ─────────────────────────────────
// Assembly modules only. Three states:
//   'active'  — generated documents ARE the firm's original .docx, filled.
//   'failed'  — tagging was attempted and one or more spans could not be
//               located (all-or-nothing): rebuilt output, failures listed.
//   null      — never attempted (created before pkg39, or no stored original):
//               rebuilt output, with a re-extract offer for Word originals.
function ExactFillStrip({ mod, failures, onReextract, reextracting }) {
  const isDocx = /\.docx$/i.test(mod.doc_filename || '');
  const status = mod.exact_fill_status;
  const wrap = (bg, border, color) => ({
    background: bg, border: `1px solid ${border}`, borderRadius: 6,
    padding: '10px 14px', marginBottom: 14,
    fontFamily: 'Arial, sans-serif', fontSize: 13, color,
  });
  const reextractBtn = (
    <button
      onClick={onReextract}
      disabled={reextracting}
      style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', padding: '5px 12px', borderRadius: 4, border: 'none', background: '#0F1838', color: '#fff', fontWeight: 600, cursor: 'pointer', marginLeft: 8, verticalAlign: 'middle' }}
    >
      {reextracting ? 'Re-extracting…' : 'Re-extract for exact-fill'}
    </button>
  );

  if (status === 'active') {
    return (
      <div style={wrap('#f0faf5', '#4caf7d', '#1a3a2a')}>
        <strong>Exact-fill active.</strong> Generated documents are your original file, filled in —
        identical fonts, spacing, margins, letterhead, headers, and footers. Placeholder remapping
        here carries into the output; edits to the static <em>text</em> of paragraphs appear only in
        the rebuilt fallback, never in exact-fill output.
        {/* pkg55 — re-extraction is also the recovery path for content the ORIGINAL
            extraction missed (a dropped signature block leaking sample data), so a
            healthy module needs the button too — quiet, with the replaces-your-work
            warning inline. */}
        {isDocx && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#3a5c4a' }}>
            Something in your document missing from the template? Re-extraction rebuilds the
            questionnaire and template from your original file (replacing corrections made here;
            the module returns to draft for review).
            <button
              onClick={onReextract}
              disabled={reextracting}
              style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', padding: '5px 12px', borderRadius: 4, border: '1px solid #4caf7d', background: '#fff', color: '#1a3a2a', fontWeight: 600, cursor: 'pointer', marginLeft: 8, verticalAlign: 'middle' }}
            >
              {reextracting ? 'Re-extracting…' : 'Re-extract from original'}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div style={wrap('#fff8e1', '#f5c842', '#7c5200')}>
        <strong>Exact-fill unavailable — using rebuilt output.</strong>{' '}
        {isDocx
          ? 'These spots in your original document could not be matched, so the whole document stays untagged (partial tagging is never used — it could leak the sample text into real documents):'
          : 'This module was not created from a Word (.docx) file, so generated documents use the rebuilt format.'}
        {isDocx && Array.isArray(failures) && failures.length > 0 && (
          <ul style={{ margin: '8px 0 4px', paddingLeft: 20 }}>
            {failures.map((f, i) => (
              <li key={i} style={{ marginBottom: 3 }}>
                {f.reason}{f.src ? <> — <code style={{ background: '#fdf3d0', padding: '0 4px', borderRadius: 3 }}>{f.src}</code></> : null}
              </li>
            ))}
          </ul>
        )}
        {isDocx && <div style={{ marginTop: 6 }}>Re-extracting sometimes resolves this:{reextractBtn}</div>}
      </div>
    );
  }

  // Never attempted. Only worth a strip when a Word original exists to offer.
  if (isDocx) {
    return (
      <div style={wrap('#f8fafb', '#dde3ec', '#555')}>
        <strong>Exact-fill available.</strong> This module was created before exact-fill mode, so
        generated documents currently use the rebuilt format. Re-extract to make the output your
        original file, filled in.{reextractBtn}
        <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
          Re-extraction replaces the questionnaire and template (including corrections made here),
          returns the module to draft for a fresh review, and uses one AI extraction.
        </div>
      </div>
    );
  }
  return null;
}

// ── pkg59 (D-58c) — exact-fill demoted to a one-line summary + expander ──────
// The full strip (failure list, re-extract buttons, warnings) is unchanged
// inside; the summary keeps the screen calm. Failed status opens expanded —
// a fidelity problem should not be one click quieter than it used to be.
function ExactFillDetails({ mod, failures, onReextract, reextracting }) {
  const status = mod.exact_fill_status;
  const isDocx = /\.docx$/i.test(mod.doc_filename || '');
  const [open, setOpen] = useState(status === 'failed');
  if (!isDocx && status !== 'failed') return null; // same visibility rule as the strip

  const summary = status === 'active'
    ? 'Exact-fill active — generated documents are your original file, filled in.'
    : status === 'failed'
      ? `Exact-fill unavailable — using rebuilt output${Array.isArray(failures) && failures.length ? ` (${failures.length} unmatched spot${failures.length === 1 ? '' : 's'})` : ''}.`
      : 'Exact-fill available — re-extract to make output your original file, filled.';
  const color = status === 'active' ? '#0F1838' : status === 'failed' ? '#7c5200' : '#555';

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'Arial, sans-serif', fontSize: 12, color, flexWrap: 'wrap' }}>
        <span>{summary}</span>
        <button
          onClick={() => setOpen(o => !o)}
          style={{ background: 'none', border: 'none', color: '#0F1838', cursor: 'pointer', textDecoration: 'underline', fontSize: 12, fontFamily: 'Arial, sans-serif', padding: 0 }}
        >
          {open ? 'Hide details' : 'Details & re-extract'}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 8 }}>
          <ExactFillStrip mod={mod} failures={failures} onReextract={onReextract} reextracting={reextracting} />
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function AdminFirmModuleReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [mod,      setMod]      = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [saving,   setSaving]   = useState(false);
  const [saveOk,   setSaveOk]   = useState(false);
  const [activating, setActivating] = useState(false);

  // Local schema state (mutated by editors)
  const [schema, setSchema] = useState(null);
  // pkg53 (D-54a) — bumping this remounts every SectionEditor so a page-level
  // bulk owner change reaches their locally cached field lists.
  const [bulkVersion, setBulkVersion] = useState(0);

  // pkg35 — assembly-only state: the document template, the unresolved
  // extraction flags, and which tab is showing.
  const [template, setTemplate] = useState(null);
  const [flags,    setFlags]    = useState([]);
  const [tab,      setTab]      = useState('template');
  const [reextracting, setReextracting] = useState(false); // pkg39

  useEffect(() => {
    adminApi.getFirmModule(id)
      .then(data => {
        setMod(data);
        setSchema(data.schema_json || { title: data.label, sections: [] });
        setTemplate(data.template_json || null);                              // pkg35
        setFlags(Array.isArray(data.extraction_flags) ? data.extraction_flags : []); // pkg35
        setTab(data.kind === 'assembly' ? 'template' : 'questionnaire');      // pkg35
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [id]);

  function updateSection(updated) {
    setSchema(prev => ({
      ...prev,
      sections: prev.sections.map(s => s.id === updated.id ? updated : s),
    }));
  }

  function deleteSection(sectionId) {
    setSchema(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId),
    }));
  }

  // pkg40 — unique-id generators for attorney-added questions/sections.
  // Field ids are scanned across the WHOLE schema AND the template's
  // placeholder references (a deleted field's id can linger in the template —
  // reusing it would silently rebind an old placeholder). issuedIds guards
  // rapid successive adds before React state settles.
  const issuedIds = useRef(new Set());
  function makeFieldId() {
    let max = 0;
    for (const sec of (schema?.sections || [])) {
      for (const f of (sec.fields || [])) {
        const m = /^f(\d+)$/.exec(f.id || '');
        if (m) max = Math.max(max, parseInt(m[1], 10));
      }
    }
    for (const b of (template?.blocks || [])) {
      for (const r of (b.content || [])) {
        if (r && r.t === 'field') {
          const m = /^f(\d+)$/.exec(r.id || '');
          if (m) max = Math.max(max, parseInt(m[1], 10));
        }
      }
    }
    let n = max + 1;
    while (issuedIds.current.has(`f${n}`)) n++;
    issuedIds.current.add(`f${n}`);
    return `f${n}`;
  }
  function makeSectionId() {
    let max = 0;
    for (const sec of (schema?.sections || [])) {
      const m = /^s(\d+)$/.exec(sec.id || '');
      if (m) max = Math.max(max, parseInt(m[1], 10));
    }
    let n = max + 1;
    while (issuedIds.current.has(`s${n}`)) n++;
    issuedIds.current.add(`s${n}`);
    return `s${n}`;
  }
  function addSection() {
    setSchema(prev => ({
      ...prev,
      sections: [...(prev.sections || []), { id: makeSectionId(), title: 'New Section', fields: [] }],
    }));
  }

  async function handleSave() {
    setSaving(true); setSaveOk(false); setError('');
    try {
      const payload = {
        label:       mod.label,
        description: mod.description,
        schema_json: schema,
      };
      if (mod.kind === 'assembly') {          // pkg35 — persist template edits + flag resolutions
        payload.template_json    = template;
        payload.extraction_flags = flags;
      }
      const updated = await adminApi.updateFirmModule(id, payload);
      setMod(updated);
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleActivate() {
    setActivating(true); setError('');
    try {
      // Save schema first, then activate.
      const payload = { schema_json: schema };
      if (mod.kind === 'assembly') {          // pkg35
        payload.template_json    = template;
        payload.extraction_flags = flags;
      }
      await adminApi.updateFirmModule(id, payload);
      await adminApi.activateFirmModule(id);
      navigate('/admin/dashboard/firm-modules');
    } catch (err) {
      setError(err.message);
      setActivating(false);
    }
  }

  // pkg35 — original-document download for side-by-side comparison.
  async function handleDownloadOriginal() {
    try {
      const blob = await adminApi.downloadFirmModuleOriginal(id);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = mod.doc_filename || 'original-document';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  }

  // pkg39 — re-extract the stored original so this module gains exact-fill.
  // Destructive to review-screen corrections, so it always confirms first and
  // the module returns to draft for a fresh review.
  async function handleReextract() {
    setReextracting(true); setError('');
    try {
      // pkg100 (D-100g/j) — the priced quote first: zero spend, real number.
      const q = await adminApi.reextractFirmModuleQuote(id);
      const priceLine = q.exempt
        ? 'Your firm is complimentary — this re-extraction uses no credits.'
        : q.free_action_available
          ? `This re-extraction uses one of your ${q.free_remaining} remaining FREE AI actions.`
          : `This re-extraction uses ${q.credits} AI credit${q.credits === 1 ? '' : 's'} ($${q.credits}.00).` +
            (q.large_document ? '\n(Large document — 2-credit tier.)' : '') +
            ` Current balance: ${q.credit_balance}.`;
      const ok = window.confirm(
        'Re-extract this module from the stored original?\n\n' +
        '• The questionnaire and document template are REPLACED — corrections made on this screen are lost.\n' +
        '• The module returns to draft and must be reviewed and activated again.\n' +
        '• Existing submissions keep their answers.\n' +
        '• Until it finishes, the current version stays in effect.\n\n' +
        priceLine + '\n\nRun it now?'
      );
      if (!ok) { setReextracting(false); return; }
      await adminApi.reextractFirmModule(id);
      // pkg100 (D-100e) — the job runs in the background; this screen's
      // schema is about to be replaced, so editing here would be editing a
      // dead copy. Back to the list, where the row shows 'Extracting…' and
      // flips when it lands.
      navigate('/admin/dashboard/firm-modules');
    } catch (err) {
      setError(err.message);
      setReextracting(false);
    }
  }

  const btnStyle = (variant = 'default') => ({
    fontSize: 13, fontFamily: 'Arial, sans-serif', cursor: 'pointer',
    padding: '8px 18px', borderRadius: 4, border: 'none',
    background: variant === 'primary' ? '#0F1838' : variant === 'success' ? '#217346' : '#eef2f7',
    color: variant === 'primary' || variant === 'success' ? '#fff' : '#0F1838',
    fontWeight: 600,
  });

  if (loading) return <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', padding: 30 }}>Loading…</div>;
  if (!mod || !schema) return <div style={{ fontFamily: 'Arial, sans-serif', color: '#c00', padding: 30 }}>{error || 'Module not found.'}</div>;

  return (
    <div style={{ maxWidth: 780, margin: '0 auto' }}>
      <div style={{ marginBottom: 6 }}>
        <button
          onClick={() => navigate('/admin/dashboard/firm-modules')}
          style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, background: 'none', border: 'none', color: '#0F1838', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
        >
          ← Back to Document Modules
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title" style={{ marginBottom: 2 }}>
            {mod.status === 'draft' ? 'Review & Activate: ' : 'Edit Fields: '}{mod.label}
          </h1>
          {/* pkg59 (D-58c) — status at a glance; exact-fill detail lives in the expander below */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', margin: '4px 0 4px' }}>
            <span style={{
              fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 3, fontWeight: 600,
              background: mod.status === 'active' ? '#f0faf5' : mod.status === 'draft' ? '#fff8e1' : '#f5f5f5',
              color:      mod.status === 'active' ? '#1a3a2a' : mod.status === 'draft' ? '#7c5200' : '#555',
              border: `1px solid ${mod.status === 'active' ? '#4caf7d' : mod.status === 'draft' ? '#f5c842' : '#ccc'}`,
            }}>
              {mod.status === 'active' ? 'Active' : mod.status === 'draft' ? 'Draft' : 'Inactive'}
            </span>
            {mod.kind === 'assembly' && /\.docx?$/i.test(mod.doc_filename || '') && (
              mod.exact_fill_status === 'active' ? (
                <span title="Generated documents are your original file, filled in place." style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 3, background: '#f0faf5', color: '#1a3a2a', border: '1px solid #4caf7d', fontWeight: 600 }}>
                  Exact-fill
                </span>
              ) : (
                <span title="Generated documents use the rebuilt format — see the exact-fill details below." style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 3, background: '#f5f5f5', color: '#666', border: '1px solid #ccc', fontWeight: 600 }}>
                  Rebuilt output
                </span>
              )
            )}
          </div>
          {mod.status === 'draft' && (
            <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', margin: 0 }}>
              {mod.kind === 'assembly'
                ? 'Review how the AI mapped your document. Fix any flagged mappings, adjust the questionnaire, then activate.'
                : 'Claude extracted the fields below from your document. Rename, reorder (drag ⠿), or delete fields before activating.'}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {mod.kind === 'assembly' && ( /* pkg35 — compare against the uploaded source */
            <button style={btnStyle()} onClick={handleDownloadOriginal}>
              Download Original
            </button>
          )}
          <button style={btnStyle()} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : saveOk ? '✓ Saved' : 'Save Changes'}
          </button>
          {mod.status === 'draft' && (
            <button
              style={{ ...btnStyle('primary'), opacity: (mod.kind === 'assembly' && flags.length > 0) ? 0.5 : 1 }}
              onClick={handleActivate}
              disabled={activating || saving || (mod.kind === 'assembly' && flags.length > 0)}
              title={mod.kind === 'assembly' && flags.length > 0 ? 'Resolve the flagged mappings first' : undefined}
            >
              {activating ? 'Activating…' : 'Save & Activate'}
            </button>
          )}
        </div>
      </div>

      {/* pkg59 (D-58c) — the five strips consolidated into one Document
          Settings card. Every row saves instantly (completed-by via the
          surgical PATCH), so the header's promise is true for all of them. */}
      <div style={{ background: '#fff', border: '1px solid #dde3ec', borderRadius: 6, marginBottom: 14 }}>
        <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, fontWeight: 700, color: '#0F1838' }}>Document settings</span>
          <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#aaa' }}>changes save instantly</span>
        </div>
        {/* pkg42 — audience switch (internal ↔ client portal) */}
        <AudienceStrip
          mod={mod}
          onChanged={updated => setMod(prev => ({ ...prev, ...updated }))}
          onError={msg => setError(msg)}
        />
        {/* pkg43/pkg59 — who completes the fields (module default), instant save */}
        <CompletedByStrip
          mod={mod}
          schema={schema}
          onSaved={(updated, v) => {
            setMod(prev => ({ ...prev, ...updated }));
            setSchema(prev => ({ ...prev, completed_by: v })); /* local edits stay; only the default flips */
          }}
          onError={msg => setError(msg)}
        />
        {/* pkg41 — client acknowledgment toggle (client-portal assembly only) */}
        <AckToggleStrip
          mod={mod}
          onToggled={updated => setMod(updated)}
          onError={msg => setError(msg)}
        />
        {/* pkg56 — submission email toggle (client-portal assembly only) */}
        <NotifyToggleStrip
          mod={mod}
          onToggled={updated => setMod(updated)}
          onError={msg => setError(msg)}
        />
      </div>

      {/* pkg39/pkg59 — exact-fill fidelity, one line + expander (assembly only) */}
      {mod.kind === 'assembly' && (
        <ExactFillDetails
          mod={mod}
          failures={mod.exact_fill_failures}
          onReextract={handleReextract}
          reextracting={reextracting}
        />
      )}

      {/* pkg59 (D-58a) — the Signatures panel: every signature line in one place */}
      {mod.kind === 'assembly' && template && (
        <SignaturesPanel schema={schema} template={template} setTemplate={setTemplate} setTab={setTab} />
      )}

      {/* pkg35 — flag gate notice + tab bar for assembly modules */}
      {mod.kind === 'assembly' && flags.length > 0 && mod.status === 'draft' && (
        <div style={{ background: '#fff8e1', border: '1px solid #f5c842', borderRadius: 4, padding: '8px 14px', marginBottom: 14, fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#7c5200' }}>
          {flags.length} flagged mapping{flags.length === 1 ? '' : 's'} to review before this module can go live — see the Document Template tab.
        </div>
      )}
      {mod.kind === 'assembly' && (
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #dde3ec', marginBottom: 18 }}>
          {[
            { key: 'template',      label: `Document Template${flags.length ? ` (${flags.length})` : ''}` },
            { key: 'questionnaire', label: 'Questionnaire' },
            { key: 'preview',       label: 'Preview' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: tab === t.key ? 700 : 400,
                padding: '9px 16px', border: 'none', background: 'none', cursor: 'pointer',
                color: tab === t.key ? '#0F1838' : '#666',
                borderBottom: tab === t.key ? '2px solid #0F1838' : '2px solid transparent',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '10px 14px', marginBottom: 16, fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#b91c1c' }}>
          {error}
        </div>
      )}

      {/* pkg35 — assembly tabs: template + preview */}
      {mod.kind === 'assembly' && tab === 'template' && template && (
        <AssemblyTemplateTab
          schema={schema} template={template} setTemplate={setTemplate}
          flags={flags} setFlags={setFlags}
          setSchema={setSchema} makeFieldId={makeFieldId} makeSectionId={makeSectionId}
          exactFillActive={mod.exact_fill_status === 'active'}
        />
      )}
      {mod.kind === 'assembly' && tab === 'template' && !template && (
        <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', padding: 20, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 6 }}>
          This module has no document template (it may have been created before the redesign). The Questionnaire tab still works normally.
        </div>
      )}
      {mod.kind === 'assembly' && tab === 'preview' && template && (
        <AssemblyPreviewTab schema={schema} template={template} />
      )}

      {(mod.kind !== 'assembly' || tab === 'questionnaire') && (<>
      {/* Module metadata */}
      <div style={{ background: '#fff', border: '1px solid #dde3ec', borderRadius: 6, padding: 16, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 3 }}>Module name</label>
            <input
              type="text"
              value={mod.label}
              onChange={e => setMod(prev => ({ ...prev, label: e.target.value }))}
              style={{ width: '100%', padding: '7px 10px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ flex: 2, minWidth: 260 }}>
            <label style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 3 }}>Description (shown to clients)</label>
            <input
              type="text"
              value={mod.description || ''}
              onChange={e => setMod(prev => ({ ...prev, description: e.target.value }))}
              style={{ width: '100%', padding: '7px 10px', fontSize: 13, fontFamily: 'Arial, sans-serif', border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box' }}
            />
          </div>
        </div>
      </div>

      {/* pkg53 (D-54a) — one-click owner for EVERY question in the module */}
      {mod.kind === 'assembly' && mod.audience === 'client' && (schema.sections || []).length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #dde3ec', borderRadius: 6, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555' }}>
          <span style={{ fontWeight: 700, color: '#0F1838' }}>Set ALL questions to:</span>
          {[['attorney', 'Attorney'], ['client', 'Client'], ['default', 'Module default']].map(([v, label]) => (
            <button
              key={v}
              onClick={() => {
                setSchema(prev => ({
                  ...prev,
                  sections: (prev.sections || []).map(sec => ({
                    ...sec,
                    fields: (sec.fields || []).map(f => {
                      if (f.filled_by === 'auto') return f;
                      if (v === 'default') {
                        const c = { ...f };
                        delete c.filled_by;
                        delete c.record_key;
                        return c;
                      }
                      return { ...f, filled_by: v };
                    }),
                  })),
                }));
                setBulkVersion(n => n + 1);
              }}
              style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', padding: '4px 12px', borderRadius: 10, border: '1px solid #c9d4e3', background: '#f8fafb', color: '#0F1838', cursor: 'pointer', fontWeight: 600 }}
            >
              {label}
            </button>
          ))}
          <span style={{ fontSize: 12, color: '#aaa' }}>Auto questions keep their source. Remember to Save Changes.</span>
        </div>
      )}

      {/* Sections */}
      {(schema.sections || []).length === 0 && (
        <div style={{ fontFamily: 'Arial, sans-serif', color: '#888', padding: 20, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 6 }}>
          No sections. All fields were removed.
        </div>
      )}

      {(schema.sections || []).map(section => (
        <SectionEditor
          key={`${section.id}-${bulkVersion}`} /* pkg53 — remount after page-level bulk change */
          section={section}
          onUpdate={updateSection}
          onDelete={deleteSection}
          makeFieldId={makeFieldId}
          showOwnership={mod.kind === 'assembly' && mod.audience === 'client'}  /* pkg43 */
          defaultOwner={moduleCompletedBy(schema) === 'attorney' ? 'attorney' : 'client'} /* pkg43 */
        />
      ))}

      {/* pkg40 — Add section */}
      <button
        onClick={addSection}
        style={{ fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '9px 16px', borderRadius: 6, border: '1px dashed #b7c4d6', background: '#fff', color: '#0F1838', cursor: 'pointer', fontWeight: 600, width: '100%' }}
      >
        + Add section
      </button>
      </>)}

      <div style={{ marginTop: 24, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button style={btnStyle()} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        {mod.status === 'draft' && (
          <button
            style={{ ...btnStyle('primary'), opacity: (mod.kind === 'assembly' && flags.length > 0) ? 0.5 : 1 }}
            onClick={handleActivate}
            disabled={activating || saving || (mod.kind === 'assembly' && flags.length > 0)}
            title={mod.kind === 'assembly' && flags.length > 0 ? 'Resolve the flagged mappings first' : undefined}
          >
            {activating ? 'Activating…' : 'Save & Activate'}
          </button>
        )}
        {mod.status === 'active' && (
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#0F1838', alignSelf: 'center', fontWeight: 600 }}>
            ✓ This module is live. Changes saved here update the client form immediately.
          </div>
        )}
      </div>
    </div>
  );
}
